import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NotificationService } from 'src/app/shared/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { map } from 'rxjs/operators';
import moment from  'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { environment } from 'src/environments/environment';
@Injectable({
  providedIn: 'root'
})
export class CompanyUserService {

  constructor(
    private afs: AngularFirestore,
    private notifierService: NotificationService,
    private authService: AuthService,
    private http: HttpClient
  ) {

  }

  addCompany(object) {
    object.updatedOn = moment().unix();
    object.createdOn = moment().unix();
    this.afs.collection('company')
      .add(object).then(data => {
        this.notifierService.showNotification('success', "Successfully added");
      })
      .catch(err => {
        this.notifierService.showNotification('error', "Some issue in adding");
      })
  }


  updateCompanyToUser(id, data) {
    let object = {}
    object['updatedOn'] = moment().unix();
    object['companyId'] = data;

    this.afs.collection('users').doc(id)
      .update(object).then(data => {
        this.notifierService.showNotification('success', "Successfully updated");
      })
      .catch(err => {
        this.notifierService.showNotification('error', "Some issue in updating");
      })
  }

  // Creates the Auth account + users/{uid} doc through a super-admin-gated
  // endpoint so Auth errors (password policy, invalid/duplicate email) come
  // straight back to the operator instead of dying silently in a trigger.
  // Returns {success, message} (rather than throwing) so the caller can keep
  // the form open and show the reason inline instead of losing what the
  // operator typed.
  async addUser(object): Promise<{ success: boolean, message?: string }> {
    try {
      const idToken = await this.authService.getToken();
      const data: any = await this.http.post(
        environment.apiUrl + '/widgets/adminUsers/create',
        JSON.stringify({
          email: object.email,
          password: object.password,
          Name: object.Name,
          accountType: object.accountType || 'user'
        }),
        { headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }) }
      ).toPromise();

      if (data && data.success) {
        this.notifierService.showNotification('success', data.invited
          ? 'User created — an email was sent so they can set their own password'
          : 'User created, but the invite email could not be sent — share the password shown above with them directly');
        return { success: true };
      }
      const message = (data && data.message) || 'Could not create user';
      this.notifierService.showNotification('error', message);
      return { success: false, message };
    } catch (err) {
      console.log('addUser error', err);
      const message = (err.error && err.error.message) || err.message || 'Could not create user';
      this.notifierService.showNotification('error', message);
      return { success: false, message };
    }
  }

  getUsers$() {
    return this.afs.collection('users').snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {
        return {
          ...obj.payload.doc.data()  as object,
          userId: obj.payload.doc.id,
          roles: Object.keys(obj.payload.doc.data()['roles']).filter(function (id) {
            return obj.payload.doc.data()['roles'][id]
          }) 
        }
      })
    }))
  }

  getUsers() {
    return this.afs.collection('users').get().pipe(map(doc => {
      return doc.docs.map(obj => {
        return {
          ...obj.data() as object,
          userId: obj.id
        }
      })
    }))
  }

  getCompanies$() {
    return this.afs.collection('company').snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {
        return {
          ...obj.payload.doc.data() as object,
          companyId: obj.payload.doc.id
        }
      })
    }))
  }

  getCompanyById$(companyId: string) {
    return this.afs.collection('company').doc(companyId).snapshotChanges().pipe(map(doc => {
      if (!doc.payload.exists) {
        return null;
      }

      return {
        ...doc.payload.data() as object,
        companyId: doc.payload.id
      };
    }))
  }


  getSitesByCompanyId$(id: string) {
    return this.afs.collection("company").doc(id).collection('sites').snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {
        return {
          name: obj.payload.doc.data()['name'],
          siteId: obj.payload.doc.id
        }
      })
    }))
  }


  updateRoles(uid, roles) {
    console.log(uid,roles)
    let object = {};
    object['updatedOn'] = moment().unix();
    object['roles'] = roles;

    this.afs.collection('users').doc(uid).update(object)
      .then(() => this.notifierService.showNotification('success', "Successfully updated!"))
      .catch(err => {
        console.log(err)
        this.notifierService.showNotification('error', "Some issue in updating")
      })
  }

  updateUserSites(uid, sites) {
    let object = {};
    object['updatedOn'] = moment().unix();
    object['sites'] = sites;

    this.afs.collection('users').doc(uid).update(object)
      .then(() => this.notifierService.showNotification('success', "Successfully updated!"))
      .catch(err => {
        console.log(err)
        this.notifierService.showNotification('error', "Some issue in updating")
      })
  }

  updateUser(uid, obj) {
    obj['updatedOn'] = moment().unix();

    this.afs.collection('users').doc(uid).update(obj)
      .then(() => this.notifierService.showNotification('success', "Successfully updated!"))
      .catch(err => {
        console.log(err)
        this.notifierService.showNotification('error', "Some issue in updating")
      })
  }

  resetPassword(email) {
    this.authService.resetPassword(email).then(() => {
      this.notifierService.showNotification('success', "Password reset email generated")
    }).catch(err => {
      console.log(err)
      this.notifierService.showNotification('error', "Some issue in email generating")
    })
  }

  updateCompany(companyId, obj) {
    const updatedObject = {
      ...obj,
      updatedOn: moment().unix()
    };

    return this.afs.collection('company').doc(companyId).update(updatedObject)
      .then(() => this.notifierService.showNotification('success', "Successfully updated!"))
      .catch(err => {
        console.log(err)
        this.notifierService.showNotification('error', "Some issue in updating")
        throw err;
      })
  }

  // Enable/disable an admin-dashboard operator's login. Goes through a
  // super-admin-gated cloud endpoint (not a direct Firestore write) because
  // it also has to disable the underlying Firebase Auth account.
  async setAccountDisabled(uid: string, disabled: boolean, reason?: string): Promise<boolean> {
    try {
      const idToken = await this.authService.getToken();
      const data: any = await this.http.post(
        environment.apiUrl + '/widgets/adminUsers/setDisabled',
        JSON.stringify({ uid, disabled, reason: reason || null }),
        { headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }) }
      ).toPromise();

      if (data && data.success) {
        this.notifierService.showNotification('success', disabled ? 'Account disabled' : 'Account enabled');
        return true;
      }
      this.notifierService.showNotification('error', (data && data.message) || 'Could not update account');
      return false;
    } catch (err) {
      console.log(err);
      this.notifierService.showNotification('error', (err.error && err.error.message) || 'Could not update account');
      return false;
    }
  }

  delete(uid) {
    this.afs.collection('users').doc(uid).delete()
      .then(() => this.notifierService.showNotification('success', "Successfully deleted!"))
      .catch(err => {
        console.log(err)
        this.notifierService.showNotification('error', "Some issue in updating")
      })
  }

}
